import { DragGesture } from '@use-gesture/vanilla';
import type { VideoSlide } from 'Api/videos';
import { MediaQueryController } from 'Components/controllers/media-query-controller';
import 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';
import 'Components/vm-modal/vm-modal';
import type { VmModal } from 'Components/vm-modal/vm-modal';
import 'Components/vm-slider/vm-slider';
import type { Contributor } from 'Components/vm-video-card/vm-video-card';
import { html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { keyed } from 'lit/directives/keyed.js';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import Styles from './vm-video-slides-viewer.scss';

type StepDirection = 'previous' | 'next';

export type SlideChangeMethod = 'keyboard' | 'swipe' | 'tap' | 'thumbnail' | 'button';

export interface SlideChangeDetail {
    /** Array indices, not `slide_number`. */
    fromIndex: number;
    toIndex: number;
    slideId: number;
    method: SlideChangeMethod;
}

const PUBLISHED_DATE_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

/**
 * Slides viewer built on top of `vm-modal` in its `lightbox` variant: the current slide
 * above a strip of numbered thumbnails, with the video card below. Navigate by thumbnail,
 * swipe, tapping the left / right half of the slide, or the arrow keys / Home / End.
 * Mobile landscape shows only the slide. A single-slide deck has no thumbnail strip, tap zones
 * or counter.
 *
 * On desktop it becomes a card: the video header on top, a vertical thumbnail rail beside
 * the slide, and previous / next buttons below it.
 *
 * Every opening starts on the first slide (pending future product change).
 *
 * @fires vm-video-slides-viewer:change - User navigation changed the slide. Detail: `SlideChangeDetail`.
 * @fires vm-video-slides-viewer:watch-video - "Watch full video" was clicked; the viewer then closes.
 * @fires vm-close - The viewer closed.
 *
 * @example
 * ```html
 * <vm-video-slides-viewer></vm-video-slides-viewer>
 * <script type="module">
 *   const viewer = document.querySelector('vm-video-slides-viewer');
 *   viewer.addEventListener('vm-video-slides-viewer:watch-video', () => player.play());
 *   viewer.videoTitle = 'Trial update';
 *   viewer.publisher = { name: 'Jane Doe' };
 *   viewer.slides = await fetchSlides(slug);
 *   viewer.open = true;
 * </script>
 * ```
 */
@customElement('vm-video-slides-viewer')
export class VmVideoSlidesViewer extends LitElement {
    static override styles = Styles;

    /** Is the viewer visible. */
    @property({ type: Boolean, reflect: true })
    open = false;

    /** The deck, in display order. */
    @property({ type: Array })
    slides: readonly VideoSlide[] = [];

    @property({ type: String, attribute: 'video-title' })
    videoTitle = '';

    /** JSON `{name, href?}` object. */
    @property({ type: Object })
    publisher: Contributor | null = null;

    /** ISO 8601 timestamp. */
    @property({ type: String, attribute: 'published-at' })
    publishedAt = '';

    /** Shown on desktop only. */
    @property({ type: String, attribute: 'video-thumbnail-url' })
    videoThumbnailUrl = '';

    @state()
    private _currentSlideIndex = 0;

    /** The latest tap or swipe; a new `id` remounts the arrow, restarting its animation. */
    @state()
    private _arrowFlash: { direction: StepDirection; id: number } | null = null;

    @query('vm-modal')
    private _modal!: VmModal;

    @query('.vm-video-slides-viewer__slide')
    private _slide!: HTMLDivElement;

    private _gesture: DragGesture | null = null;

    private _desktop = new MediaQueryController(this, DESKTOP_BREAKPOINT_QUERY);

    override willUpdate(changed: PropertyValues<this>): void {
        if (changed.has('open') && this.open) {
            this._currentSlideIndex = 0;
            this._arrowFlash = null;
        }
    }

    override updated(changed: PropertyValues<this>): void {
        if (!changed.has('open')) return;
        if (this.open) {
            this._attachGesture();
        } else {
            this._detachGesture();
        }
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._detachGesture();
    }

    /** Returns whether the slide changed. */
    private _select(toIndex: number, method: SlideChangeMethod): boolean {
        const fromIndex = this._currentSlideIndex;
        const slide = this.slides[toIndex];
        if (toIndex === fromIndex || !slide) return false;

        this._currentSlideIndex = toIndex;
        this.dispatchEvent(
            new CustomEvent<SlideChangeDetail>('vm-video-slides-viewer:change', {
                detail: { fromIndex, toIndex, slideId: slide.id, method },
                bubbles: true,
                composed: true,
            }),
        );
        return true;
    }

    /** Moves one slide and flashes the arrow on that side. */
    private _step(direction: StepDirection, method: 'swipe' | 'tap'): void {
        const target = this._currentSlideIndex + (direction === 'next' ? 1 : -1);
        if (!this._select(target, method)) return;
        this._arrowFlash = { direction, id: (this._arrowFlash?.id ?? 0) + 1 };
    }

    private _attachGesture(): void {
        if (this._gesture || !this._slide) return;
        this._gesture = new DragGesture(
            this._slide,
            // swipeX is 1 for a rightward flick, i.e. towards the previous slide.
            ({ swipe: [swipeX] }) => {
                if (swipeX) this._step(swipeX > 0 ? 'previous' : 'next', 'swipe');
            },
            { axis: 'x', filterTaps: true, pointer: { keys: false } },
        );
    }

    private _detachGesture(): void {
        this._gesture?.destroy();
        this._gesture = null;
    }

    private async _onKeydown(e: KeyboardEvent): Promise<void> {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        const targets: Record<string, number> = {
            ArrowLeft: this._currentSlideIndex - 1,
            ArrowUp: this._currentSlideIndex - 1,
            ArrowRight: this._currentSlideIndex + 1,
            ArrowDown: this._currentSlideIndex + 1,
            Home: 0,
            End: this.slides.length - 1,
        };
        const target = targets[e.key];
        if (target === undefined) return;
        e.preventDefault();
        if (!this._select(target, 'keyboard')) return;

        // Focus follows selection, or Space / Enter would re-select the previously focused thumbnail.
        if (!(e.target instanceof HTMLElement) || !e.target.matches('.vm-video-slides-viewer__thumbnail')) return;
        await this.updateComplete;
        // preventScroll: vm-slider already scrolls the active thumbnail into view.
        this.renderRoot
            .querySelectorAll<HTMLButtonElement>('.vm-video-slides-viewer__thumbnail')
            [target]?.focus({ preventScroll: true });
    }

    private _renderTapZone(direction: StepDirection) {
        const target = this._currentSlideIndex + (direction === 'next' ? 1 : -1);
        // aria-disabled rather than disabled, so a focused zone keeps focus at the deck's end.
        const atEnd = target < 0 || target >= this.slides.length;
        const flash = this._arrowFlash?.direction === direction ? this._arrowFlash : null;
        return html`
            <button
                type="button"
                class="vm-video-slides-viewer__tap-zone vm-video-slides-viewer__tap-zone--${direction}"
                aria-label="${direction === 'next' ? 'Next' : 'Previous'} slide"
                aria-disabled=${ifDefined(atEnd ? 'true' : undefined)}
                @click=${() => this._step(direction, 'tap')}
            >
                ${flash
                    ? keyed(
                          flash.id,
                          html`<span class="vm-video-slides-viewer__nav-arrow">
                              <vm-icon name=${direction === 'next' ? 'chevron-right' : 'chevron-left'}></vm-icon>
                          </span>`,
                      )
                    : nothing}
            </button>
        `;
    }

    private _onWatchVideo(): void {
        this.dispatchEvent(new CustomEvent('vm-video-slides-viewer:watch-video', { bubbles: true, composed: true }));
        this._modal.requestClose();
    }

    private _onModalClose(): void {
        // Keep our `open` in sync when the modal closes itself (Escape, backdrop, or its
        // built-in close button). The modal's `vm-close` bubbles past this listener and
        // is retargeted to this host, so consumers see a single event.
        this.open = false;
    }

    private get _metaLine(): string {
        const publishedAt = this.publishedAt ? new Date(this.publishedAt) : null;
        const published =
            publishedAt && !Number.isNaN(publishedAt.getTime())
                ? publishedAt.toLocaleDateString('en-US', PUBLISHED_DATE_FORMAT)
                : '';
        return [this.publisher?.name, published].filter(Boolean).join(' • ');
    }

    private _renderThumbnail(slide: VideoSlide, index: number) {
        return html`
            <div class="vm-video-slides-viewer__thumbnail-item" role="listitem">
                <button
                    type="button"
                    class="vm-video-slides-viewer__thumbnail"
                    aria-label="Slide ${index + 1}"
                    aria-current=${ifDefined(index === this._currentSlideIndex ? 'true' : undefined)}
                    @click=${() => this._select(index, 'thumbnail')}
                >
                    <img
                        class="vm-video-slides-viewer__thumbnail-image"
                        src=${slide.thumbnail_url || slide.image_url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        draggable="false"
                    />
                    <span class="vm-video-slides-viewer__thumbnail-number" aria-hidden="true">${index + 1}</span>
                </button>
            </div>
        `;
    }

    private _renderInfo() {
        const meta = this._metaLine;
        const desktop = this._desktop.matches;
        return html`
            <div class="vm-video-slides-viewer__info">
                ${this.videoThumbnailUrl
                    ? html`<img class="vm-video-slides-viewer__video-thumbnail" src=${this.videoThumbnailUrl} alt="" />`
                    : nothing}
                <div class="vm-video-slides-viewer__details">
                    ${this.videoTitle
                        ? html`<h2 class="vm-video-slides-viewer__title">${this.videoTitle}</h2>`
                        : nothing}
                    ${meta ? html`<p class="vm-video-slides-viewer__meta">${meta}</p>` : nothing}
                </div>
                <vm-button
                    class="vm-video-slides-viewer__watch"
                    variant="solid"
                    color-scheme="primary"
                    size=${desktop ? 'md' : 'xl'}
                    ?full-width=${!desktop}
                    @click=${this._onWatchVideo}
                >
                    <vm-icon slot="icon-leading" name="play-circle"></vm-icon>
                    Watch full video
                </vm-button>
            </div>
        `;
    }

    private _renderNav() {
        const index = this._currentSlideIndex;
        return html`
            <div class="vm-video-slides-viewer__nav">
                <vm-button
                    variant="outline"
                    color-scheme="neutral"
                    size="lg"
                    icon-only
                    aria-label="Previous slide"
                    ?disabled=${index === 0}
                    @click=${() => this._select(index - 1, 'button')}
                >
                    <vm-icon slot="icon-leading" name="chevron-left"></vm-icon>
                </vm-button>
                <vm-button
                    variant="outline"
                    color-scheme="neutral"
                    size="lg"
                    icon-only
                    aria-label="Next slide"
                    ?disabled=${index >= this.slides.length - 1}
                    @click=${() => this._select(index + 1, 'button')}
                >
                    <vm-icon slot="icon-leading" name="chevron-right"></vm-icon>
                </vm-button>
            </div>
        `;
    }

    override render() {
        const slide = this.slides[this._currentSlideIndex];
        const position = `${this._currentSlideIndex + 1} of ${this.slides.length}`;
        const hasNavigation = this.slides.length > 1;

        return html`
            <vm-modal
                variant="lightbox"
                dialog-label="Slides viewer"
                ?open=${this.open}
                @vm-close=${this._onModalClose}
                @keydown=${this._onKeydown}
            >
                <div class="vm-video-slides-viewer">
                    <div class="vm-video-slides-viewer__stage">
                        <div class="vm-video-slides-viewer__pane">
                            <div class="vm-video-slides-viewer__slide">
                                ${slide
                                    ? html`<img
                                          class="vm-video-slides-viewer__image"
                                          src=${slide.image_url}
                                          alt=${slide.title || `Slide ${this._currentSlideIndex + 1}`}
                                          draggable="false"
                                      />`
                                    : nothing}
                                ${hasNavigation
                                    ? html`${this._renderTapZone('previous')} ${this._renderTapZone('next')}`
                                    : nothing}
                            </div>
                            ${hasNavigation ? this._renderNav() : nothing}
                        </div>
                        ${hasNavigation
                            ? html`<vm-slider
                                  class="vm-video-slides-viewer__thumbnails"
                                  .orientation=${this._desktop.matches ? 'vertical' : 'horizontal'}
                                  .activeIndex=${this._currentSlideIndex}
                              >
                                  ${this.slides.map((s, index) => this._renderThumbnail(s, index))}
                              </vm-slider>`
                            : nothing}
                    </div>
                    ${this._renderInfo()}
                    ${hasNavigation
                        ? html`<p class="vm-video-slides-viewer__counter" aria-hidden="true">${position}</p>`
                        : nothing}
                    ${this.slides.length
                        ? html`<p class="vm-video-slides-viewer__status" role="status">Slide ${position}</p>`
                        : nothing}
                </div>
            </vm-modal>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-slides-viewer': VmVideoSlidesViewer;
    }
    interface HTMLElementEventMap {
        'vm-video-slides-viewer:change': CustomEvent<SlideChangeDetail>;
        'vm-video-slides-viewer:watch-video': CustomEvent<void>;
    }
}
